import React, { useState, useEffect } from 'react';
import { Layout, Tabs, Button, message, Typography, Space, Breadcrumb, Spin, Empty, Statistic, Row, Col, Card, Progress } from 'antd';
import { ArrowLeftOutlined, ReloadOutlined, DatabaseOutlined, HomeOutlined, CheckCircleOutlined, AppstoreOutlined, BlockOutlined, BankOutlined, TagsOutlined, ShoppingOutlined } from '@ant-design/icons';
import axios from 'axios';
import TableBuilder from './TableBuilder';
import HotelImageGallery from './HotelImageGallery';
import { API_URL } from '../config';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

interface HotelDataDashboardProps {
  hotelId: number;
  hotelName: string;
  groupName: string;
  groupId: number;
  activeTab?: string;
  onBack: () => void;
  onBackToGroups: () => void;
  onTabChange?: (tabKey: string) => void;
  onNamesLoaded?: (hotelName: string, groupName: string) => void;
}

const HotelDataDashboard: React.FC<HotelDataDashboardProps> = ({
  hotelId,
  hotelName,
  groupName,
  groupId,
  activeTab,
  onBack,
  onBackToGroups,
  onTabChange,
  onNamesLoaded,
}) => {
  const [hotelData, setHotelData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadedHotelName, setLoadedHotelName] = useState(hotelName);
  const [loadedGroupName, setLoadedGroupName] = useState(groupName);

  useEffect(() => {
    loadHotelSummary();
  }, [hotelId]);

  const loadHotelSummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/hotels/${hotelId}/data`);
      setHotelData(response.data);

      // Load names if not provided
      if (!hotelName || !groupName) {
        const hotel = response.data.hotel;
        setLoadedHotelName(hotel.name);

        if (hotel.hotelGroup) {
          setLoadedGroupName(hotel.hotelGroup.name);
          onNamesLoaded?.(hotel.name, hotel.hotelGroup.name);
        }
      }
    } catch (error: any) {
      message.error('Failed to load hotel data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshData = async () => {
    setRefreshing(true);
    const hide = message.loading('Syncing latest data from PMS...');

    try {
      const response = await axios.post(`${API_URL}/pms/sync`, { hotelId });

      if (response.data.success) {
        hide();
        message.success('Data refreshed successfully!');
        await loadHotelSummary();
      }
    } catch (error: any) {
      hide();
      message.error(error.response?.data?.message || 'Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <Spin size="large" tip="Loading hotel data..." />
      </div>
    );
  }

  if (!hotelData || !hotelData.data) {
    return (
      <div style={{ padding: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          style={{ marginBottom: 16 }}
        >
          Back to Hotels
        </Button>
        <Card>
          <Empty
            image={<DatabaseOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
            description={
              <div>
                <Title level={4}>No data available</Title>
                <Text type="secondary">
                  Click the Refresh button in the hotel list to fetch data from the PMS
                </Text>
              </div>
            }
          />
        </Card>
      </div>
    );
  }

  const tabItems = [
    {
      key: 'overview',
      label: 'Overview',
      children: (() => {
        const s = hotelData.summary;
        const pushCards = [
          {
            title: 'Room Types',
            total: s.roomTypes,
            pushed: s.roomTypesPushed || 0,
            icon: <AppstoreOutlined />,
            color: '#1890ff',
          },
          {
            title: 'Floors',
            total: s.floors || 0,
            pushed: s.floorsPushed || 0,
            icon: <BlockOutlined />,
            color: '#722ed1',
          },
          {
            title: 'Rooms',
            total: s.rooms,
            pushed: s.roomsPushed || 0,
            icon: <BankOutlined />,
            color: '#13c2c2',
          },
          {
            title: 'Features',
            total: s.roomFeatures,
            pushed: s.roomFeaturesPushed || 0,
            icon: <TagsOutlined />,
            color: '#faad14',
          },
          {
            title: 'Products',
            total: s.products,
            pushed: null,
            icon: <ShoppingOutlined />,
            color: '#f5222d',
          },
        ];

        const totalPushable = pushCards.filter(c => c.pushed !== null).reduce((sum, c) => sum + c.total, 0);
        const totalPushed = pushCards.filter(c => c.pushed !== null).reduce((sum, c) => sum + (c.pushed || 0), 0);
        const overallPct = totalPushable > 0 ? Math.round((totalPushed / totalPushable) * 100) : 0;

        return (
          <div style={{ padding: '24px 0' }}>
            {/* Overall Progress */}
            <Card bordered={false} style={{ marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
                <Progress
                  type="dashboard"
                  percent={overallPct}
                  size={120}
                  strokeColor={{
                    '0%': '#1890ff',
                    '100%': '#52c41a',
                  }}
                  format={(pct) => (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 24, fontWeight: 700, color: '#262626' }}>{pct}%</div>
                      <div style={{ fontSize: 11, color: '#8c8c8c' }}>Pushed</div>
                    </div>
                  )}
                />
                <div style={{ flex: 1 }}>
                  <Title level={4} style={{ margin: 0, marginBottom: 4 }}>EM Push Progress</Title>
                  <Text type="secondary">
                    {totalPushed} of {totalPushable} pushable items synced to EM
                  </Text>
                  <div style={{ display: 'flex', gap: 24, marginTop: 16, flexWrap: 'wrap' }}>
                    {pushCards.filter(c => c.pushed !== null).map(c => {
                      const pct = c.total > 0 ? Math.round((c.pushed! / c.total) * 100) : 0;
                      return (
                        <div key={c.title} style={{ minWidth: 100 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>{c.title}</Text>
                          <Progress
                            percent={pct}
                            size="small"
                            strokeColor={pct === 100 ? '#52c41a' : c.color}
                            format={() => `${c.pushed}/${c.total}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Card>

            {/* Individual Cards */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {pushCards.map(c => {
                const pct = c.pushed !== null && c.total > 0
                  ? Math.round((c.pushed / c.total) * 100)
                  : null;
                const allPushed = pct === 100;

                return (
                  <Card
                    key={c.title}
                    bordered={false}
                    style={{
                      flex: '1 1 180px',
                      minWidth: 180,
                      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                      <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        background: `${c.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 20,
                        color: c.color,
                      }}>
                        {c.icon}
                      </div>
                      <div>
                        <Text type="secondary" style={{ fontSize: 13 }}>{c.title}</Text>
                        <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1, color: '#262626' }}>
                          {c.total}
                        </div>
                      </div>
                    </div>

                    {pct !== null ? (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <Text style={{ fontSize: 12, color: allPushed ? '#52c41a' : '#8c8c8c' }}>
                            {allPushed ? (
                              <><CheckCircleOutlined style={{ marginRight: 4 }} />All pushed</>
                            ) : (
                              <>{c.pushed} of {c.total} pushed</>
                            )}
                          </Text>
                          <Text style={{ fontSize: 12, color: '#8c8c8c' }}>{pct}%</Text>
                        </div>
                        <Progress
                          percent={pct}
                          showInfo={false}
                          size="small"
                          strokeColor={allPushed ? '#52c41a' : c.color}
                        />
                      </div>
                    ) : (
                      <div style={{ height: 28 }}>
                        <Text style={{ fontSize: 12, color: '#8c8c8c' }}>No EM push required</Text>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })(),
    },
    {
      key: 'room_types',
      label: 'Room Types',
      children: (
        <TableBuilder
          dataType="room_types"
          hotelId={hotelId}
          emHotelId={hotelData?.hotel?.emHotelId}
        />
      ),
    },
    {
      key: 'room_features',
      label: 'Room Features',
      children: (
        <TableBuilder
          dataType="room_features"
          hotelId={hotelId}
          emHotelId={hotelData?.hotel?.emHotelId}
          pmsType={hotelData?.hotel?.pmsType}
        />
      ),
    },
    {
      key: 'floors',
      label: 'Floors',
      children: (
        <TableBuilder
          dataType="floors"
          hotelId={hotelId}
          emHotelId={hotelData?.hotel?.emHotelId}
          pmsType={hotelData?.hotel?.pmsType}
        />
      ),
    },
    {
      key: 'rooms',
      label: 'Rooms',
      children: (
        <TableBuilder
          dataType="rooms"
          hotelId={hotelId}
          emHotelId={hotelData?.hotel?.emHotelId}
        />
      ),
    },
    {
      key: 'products',
      label: 'Products',
      children: (
        <TableBuilder
          dataType="products"
          hotelId={hotelId}
        />
      ),
    },
    {
      key: 'images',
      label: 'Images',
      children: (
        <HotelImageGallery hotelId={hotelId} />
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: '#fff' }}>
      <Header style={{
        background: '#fff',
        padding: '8px 24px',
        borderBottom: '1px solid #f0f0f0',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        height: 'auto',
        lineHeight: 'normal',
      }}>
        <Breadcrumb
          items={[
            {
              title: (
                <a onClick={onBackToGroups} style={{ cursor: 'pointer' }}>
                  <HomeOutlined style={{ marginRight: 4 }} />
                  Hotel Groups
                </a>
              ),
            },
            {
              title: (
                <a onClick={onBack} style={{ cursor: 'pointer' }}>
                  {loadedGroupName || groupName}
                </a>
              ),
            },
            { title: loadedHotelName || hotelName },
          ]}
          style={{ marginBottom: 6, fontSize: 12 }}
        />
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Title level={3} style={{ margin: 0 }}>{loadedHotelName || hotelName}</Title>
          <Space size="small">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={handleRefreshData}
              loading={refreshing}
            >
              Refresh Data
            </Button>
            <Button
              size="small"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
            >
              Back to Hotels
            </Button>
          </Space>
        </div>
      </Header>

      <Content style={{ padding: 0, background: '#fff' }}>
        <Tabs
          activeKey={activeTab || 'overview'}
          onChange={onTabChange}
          items={tabItems}
          style={{ padding: '0 20px' }}
          size="large"
        />
      </Content>
    </Layout>
  );
};

export default HotelDataDashboard;
